
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useStripeCheckout = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Check for pending checkout on component mount
  useEffect(() => {
    const pendingCheckoutStr = sessionStorage.getItem('pendingCheckout');
    if (pendingCheckoutStr) {
      console.log('Found pending checkout on initial load, checking auth state');
      // Check auth state before processing
      supabase.auth.getSession().then(({ data, error }) => {
        if (!error && data.session) {
          console.log('User is authenticated, processing pending checkout');
          checkPendingCheckout();
        } else {
          console.log('User not authenticated, pending checkout will be processed after login');
        }
      });
    }
  }, []);

  // Function to start Stripe checkout process
  const startCheckout = async (priceId: string, interval: 'monthly' | 'annual' = 'monthly') => {
    console.log('Starting checkout for:', priceId, 'interval:', interval);
    
    // Handle free tier signup without Stripe
    if (priceId.toLowerCase() === 'free-tier' || priceId.toLowerCase() === 'free') {
      console.log('Processing free tier signup');
      navigate('/auth?mode=signup&plan=free');
      return;
    }
    
    setLoading(true);
    
    try {
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        console.log('No authenticated user found, storing checkout info and redirecting to auth page');
        // Store checkout details in session storage
        sessionStorage.setItem('pendingCheckout', JSON.stringify({ priceId, interval }));
        
        // Show a toast to inform the user
        toast.info('Please sign in or create an account to continue with your purchase');
        
        // Redirect to auth with signup mode
        navigate('/auth?mode=signup');
        return;
      }
      
      console.log('User found, initiating Stripe checkout for user:', user.id);
      
      // Determine plan type based on priceId
      const planType = getPlanType(priceId);
      
      // Call the Stripe checkout edge function
      const { data, error } = await supabase.functions.invoke('create-simple-checkout', {
        body: { 
          priceId, 
          planType,
          isAnnual: interval === 'annual',
          userId: user.id,
          userEmail: user.email,
          successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/`
        }
      });
      
      if (error) {
        console.error('Checkout function error:', error);
        throw new Error(error.message || 'Failed to initiate checkout');
      }
      
      if (data?.url) {
        console.log('Redirecting to Stripe checkout URL:', data.url);
        // Use window.location.href for a full page redirect to Stripe
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned:', data);
        throw new Error('No checkout URL returned');
      }
    } catch (error: any) {
      console.error('Error starting checkout:', error.message);
      toast.error('Failed to start checkout process');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to determine plan type from price ID or plan name
  const getPlanType = (input: string): string => {
    // Normalize the input to lowercase
    const normalized = input.toLowerCase();
    
    if (normalized.includes('starter')) return 'starter';
    if (normalized.includes('pro')) return 'pro';
    if (normalized.includes('premium')) return 'premium';
    if (normalized.includes('pay-per-report')) return 'pay-per-report';
    if (normalized.includes('free')) return 'free';
    
    // If price ID doesn't contain plan name, match based on known price IDs
    if (normalized === 'price_1redpqaps1q0yn5q0jc6zerd') return 'starter';
    if (normalized === 'price_1redviaps1q0yn5qeaz25dj2') return 'starter';
    if (normalized === 'price_1redteaps1q0yn5q9wevutor') return 'pro';
    if (normalized === 'price_1redvnaps1q0yn5qcifl7emk') return 'pro';
    if (normalized === 'price_1reduaaps1q0yn5qcu5mh3ev') return 'premium';
    if (normalized === 'price_1redwzaps1q0yn5qpareoirg') return 'premium';
    
    // Default to pay-per-report if no matching plan type
    return 'pay-per-report';
  };
  
  // Function to check for pending checkout after login/signup
  const checkPendingCheckout = () => {
    console.log('Checking for pending checkout');
    const pendingCheckoutStr = sessionStorage.getItem('pendingCheckout');
    
    if (!pendingCheckoutStr) {
      console.log('No pending checkout found');
      return false;
    }
    
    try {
      const pendingCheckout = JSON.parse(pendingCheckoutStr);
      console.log('Found pending checkout:', pendingCheckout);
      
      if (pendingCheckout) {
        const priceId = pendingCheckout.priceId || pendingCheckout.planName;
        const interval = pendingCheckout.interval || 'monthly';
        
        if (!priceId) {
          throw new Error('No price ID or plan name found in pending checkout');
        }
        
        console.log('Processing pending checkout for price ID:', priceId, 'interval:', interval);
        
        // Clear pending checkout from session storage
        sessionStorage.removeItem('pendingCheckout');
        
        // Get the current user to make sure we're authenticated
        supabase.auth.getUser().then(({ data: { user }, error }) => {
          if (error || !user) {
            console.error('User not authenticated for checkout:', error);
            toast.error('Please sign in to complete your purchase');
            navigate('/auth');
            return;
          }
          
          console.log('User authenticated, proceeding with checkout');
          // Start the checkout process after a short delay
          setTimeout(() => {
            startCheckout(priceId, interval as 'monthly' | 'annual');
          }, 500);
        });
        
        return true;
      }
    } catch (error) {
      console.error('Error processing pending checkout:', error);
      sessionStorage.removeItem('pendingCheckout');
    }
    
    return false;
  };
  
  return { startCheckout, checkPendingCheckout, loading };
};
